import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    apiGetHandover,
    apiPatchHandover,
    apiSubmitHandover,
    apiAcknowledgeHandover,
    apiCarryForwardHandover,
    apiDeleteHandover,
    apiAssignableUsers,
    formatApiError,
} from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import CategorySection from "@/components/handover/CategorySection";
import SendEmailDialog from "@/components/handover/SendEmailDialog";
import { CATEGORIES, CATEGORY_LABEL } from "@/lib/handover";
import {
    ArrowLeft,
    ClipboardCheck,
    Send,
    Trash2,
    CheckCircle2,
    AlertCircle,
    Calendar,
    Clock3,
    User as UserIcon,
    Undo2,
    Mail,
    ArrowRightCircle,
} from "lucide-react";
import { fromISO, formatLong } from "@/lib/date";

const UNASSIGNED = "__unassigned__";

export default function HandoverDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [state, setState] = useState({ handover: null, items: [], can_edit: false });
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState("");
    const [saveError, setSaveError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [emailOpen, setEmailOpen] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [confirmAck, setConfirmAck] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmCarry, setConfirmCarry] = useState(false);
    const [carrying, setCarrying] = useState(false);
    const savedSummaryRef = useRef("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiGetHandover(id);
            setState(data);
            setSummary(data.handover?.shift_summary || "");
            savedSummaryRef.current = data.handover?.shift_summary || "";
            if (data.can_edit && data.handover?.project_id) {
                try {
                    const list = await apiAssignableUsers(data.handover.project_id);
                    setCandidates(list);
                } catch {
                    setCandidates([]);
                }
            }
        } catch (err) {
            toast.error(formatApiError(err));
            navigate("/handovers");
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const handover = state.handover;
    const items = state.items;
    const canEdit = !!state.can_edit;

    const grouped = useMemo(() => {
        const m = {};
        for (const c of CATEGORIES) m[c.value] = [];
        for (const it of items) {
            (m[it.category] = m[it.category] || []).push(it);
        }
        return m;
    }, [items]);

    const openIncidentsMissingAction = useMemo(() => {
        return (grouped["Open Incident"] || []).filter(
            (i) =>
                i.item_status === "Open" && !(i.details || "").trim(),
        );
    }, [grouped]);

    const stillOpenCount = useMemo(
        () => items.filter((i) => i.item_status === "Open").length,
        [items],
    );

    async function saveSummary() {
        if (!canEdit) return;
        if (summary === savedSummaryRef.current) return;
        try {
            const updated = await apiPatchHandover(id, { shift_summary: summary });
            setState((s) => ({ ...s, handover: updated }));
            savedSummaryRef.current = summary;
        } catch (err) {
            setSaveError(formatApiError(err));
        }
    }

    async function changeIncoming(v) {
        try {
            const updated = await apiPatchHandover(id, {
                incoming_user_id: v === UNASSIGNED ? "" : v,
            });
            setState((s) => ({ ...s, handover: updated }));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function submit() {
        if (!summary.trim()) {
            toast.error("Shift Summary is required to submit");
            return;
        }
        if (openIncidentsMissingAction.length > 0) {
            toast.error(
                `Every open incident needs a next action (${openIncidentsMissingAction.length} missing)`,
            );
            return;
        }
        setSubmitting(true);
        try {
            // Save latest summary first
            if (summary !== savedSummaryRef.current) {
                await apiPatchHandover(id, { shift_summary: summary });
                savedSummaryRef.current = summary;
            }
            const updated = await apiSubmitHandover(id);
            toast.success("Handover submitted");
            setState((s) => ({ ...s, handover: updated, can_edit: false }));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    async function acknowledge() {
        try {
            const updated = await apiAcknowledgeHandover(id);
            toast.success("Handover acknowledged");
            setState((s) => ({ ...s, handover: updated }));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function remove() {
        try {
            await apiDeleteHandover(id);
            toast.success("Draft deleted");
            navigate("/handovers");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function carryForward() {
        setCarrying(true);
        try {
            const res = await apiCarryForwardHandover(id);
            const target = res.handover;
            if (res.copied === 0 && res.skipped > 0) {
                toast.success(
                    `Nothing new to copy. All ${res.skipped} still-open items are already on the next handover.`,
                );
            } else {
                toast.success(
                    `Copied ${res.copied} item${res.copied === 1 ? "" : "s"} to ${target.shift_name} · ${target.handover_date}` +
                    (res.skipped ? ` (${res.skipped} already carried)` : ""),
                );
            }
            setConfirmCarry(false);
            navigate(`/handovers/${target.handover_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCarrying(false);
        }
    }

    if (loading) {
        return (
            <Layout active="handovers">
                <div className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted">
                    Loading handover…
                </div>
            </Layout>
        );
    }
    if (!handover) {
        return (
            <Layout active="handovers">
                <div className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted">
                    Handover not found.
                </div>
            </Layout>
        );
    }

    const canAcknowledge =
        handover.status === "Submitted" &&
        user?.role !== "Client" &&
        user?.user_id !== handover.outgoing_user_id &&
        (!handover.incoming_user_id ||
            handover.incoming_user_id === user?.user_id ||
            user?.role === "Super Admin" ||
            user?.role === "Admin");

    const canSendEmail =
        (handover.status === "Submitted" || handover.status === "Acknowledged") &&
        (user?.role === "Super Admin" ||
            user?.role === "Admin" ||
            user?.role === "Team Lead" ||
            user?.user_id === handover.outgoing_user_id);

    const canCarryForward =
        handover.status === "Acknowledged" &&
        stillOpenCount > 0 &&
        (user?.role === "Super Admin" ||
            user?.role === "Admin" ||
            user?.role === "Team Lead" ||
            user?.user_id === handover.incoming_user_id);

    return (
        <Layout active="handovers">
            <div
                className="flex items-center gap-2 mb-4"
                data-testid="handover-back"
            >
                <Button
                    variant="outline"
                    onClick={() => navigate("/handovers")}
                    className="h-8 text-[12px]"
                >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                    Back
                </Button>
            </div>

            {/* Header card */}
            <div
                className="bg-white border border-ntt-border rounded-xl p-5 mb-4"
                data-testid="handover-header"
            >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-ntt-muted mb-1">
                            Handover
                        </div>
                        <h1
                            className="text-2xl font-semibold text-[#070F26]"
                            data-testid="handover-title"
                        >
                            {handover.shift_name || "Shift"} ·{" "}
                            {formatLong(fromISO(handover.handover_date))}
                        </h1>
                        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-ntt-muted">
                            <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                {handover.handover_date}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <Clock3 className="h-3.5 w-3.5" />
                                {handover.shift_name}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <UserIcon className="h-3.5 w-3.5" />
                                {handover.outgoing_user_name || "—"}
                                <span className="mx-1 text-ntt-muted">→</span>
                                {handover.incoming_user_name || (
                                    <em className="text-ntt-muted">open</em>
                                )}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                Project ·{" "}
                                <span className="font-mono">
                                    {handover.project_code || handover.project_id}
                                </span>
                            </span>
                        </div>
                        {(handover.submitted_at || handover.acknowledged_at) && (
                            <div className="mt-2 text-[11px] text-ntt-muted flex flex-wrap gap-4">
                                {handover.submitted_at && (
                                    <span data-testid="submitted-at">
                                        Submitted · {fmtDT(handover.submitted_at)}
                                    </span>
                                )}
                                {handover.acknowledged_at && (
                                    <span data-testid="acknowledged-at">
                                        Acknowledged · {fmtDT(handover.acknowledged_at)}
                                        {handover.acknowledged_by_name
                                            ? ` by ${handover.acknowledged_by_name}`
                                            : ""}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <StatusPill
                            label={handover.status}
                            tone={
                                handover.status === "Acknowledged"
                                    ? "green"
                                    : handover.status === "Submitted"
                                      ? "blue"
                                      : "gray"
                            }
                        />
                        {canSendEmail && (
                            <Button
                                variant="outline"
                                onClick={() => setEmailOpen(true)}
                                data-testid="send-email-btn"
                                className="h-9 text-[12px]"
                            >
                                <Mail className="h-3.5 w-3.5 mr-1.5" />
                                Send by email
                            </Button>
                        )}
                    </div>
                </div>

                {/* Actions */}
                {canEdit && (
                    <div className="mt-4 flex flex-wrap items-center gap-3 pt-4 border-t border-ntt-border">
                        <div className="flex items-center gap-2">
                            <label className="text-[12px] text-ntt-muted">
                                Incoming
                            </label>
                            <Select
                                value={handover.incoming_user_id || UNASSIGNED}
                                onValueChange={changeIncoming}
                            >
                                <SelectTrigger
                                    className="h-9 w-[220px]"
                                    data-testid="incoming-picker"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNASSIGNED} data-testid="incoming-unassigned">
                                        Leave open for incoming shift
                                    </SelectItem>
                                    {candidates.map((c) => (
                                        <SelectItem
                                            key={c.user_id}
                                            value={c.user_id}
                                            data-testid={`incoming-option-${c.user_id}`}
                                        >
                                            {c.full_name} · {c.role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDelete(true)}
                                data-testid="delete-draft-btn"
                                className="h-9 text-[12px] text-red-600 hover:text-red-700"
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Delete draft
                            </Button>
                            <Button
                                onClick={() => {
                                    if (!summary.trim()) {
                                        toast.error("Shift Summary is required to submit");
                                        return;
                                    }
                                    if (openIncidentsMissingAction.length > 0) {
                                        toast.error(
                                            `Every open incident needs a next action (${openIncidentsMissingAction.length} missing)`,
                                        );
                                        return;
                                    }
                                    setConfirmSubmit(true);
                                }}
                                disabled={submitting}
                                data-testid="submit-handover-btn"
                                className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                            >
                                <Send className="h-3.5 w-3.5 mr-1.5" />
                                {submitting ? "Submitting…" : "Submit handover"}
                            </Button>
                        </div>
                    </div>
                )}

                {canAcknowledge && (
                    <div
                        className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-ntt-border"
                        data-testid="acknowledge-bar"
                    >
                        <div className="text-[13px] text-[#070F26]">
                            <span className="font-medium">
                                Ready for acknowledgement.
                            </span>{" "}
                            <span className="text-ntt-muted">
                                Review the summary and each section, then confirm.
                            </span>
                        </div>
                        <Button
                            onClick={() => setConfirmAck(true)}
                            data-testid="acknowledge-btn"
                            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                            Acknowledge
                        </Button>
                    </div>
                )}

                {handover.status === "Acknowledged" && (canCarryForward || handover.carried_forward_to_handover_id) && (
                    <div
                        className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-ntt-border"
                        data-testid="carry-forward-bar"
                    >
                        <div className="text-[13px] text-[#070F26]">
                            {handover.carried_forward_to_handover_id ? (
                                <>
                                    <span className="font-medium">Items carried forward.</span>{" "}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/handovers/${handover.carried_forward_to_handover_id}`)
                                        }
                                        className="text-[#0072BC] hover:underline"
                                        data-testid="carry-forward-goto-next"
                                    >
                                        Open next handover →
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className="font-medium">
                                        {stillOpenCount} still-open item{stillOpenCount === 1 ? "" : "s"}.
                                    </span>{" "}
                                    <span className="text-ntt-muted">
                                        Carry them forward to the next shift as a new Draft.
                                    </span>
                                </>
                            )}
                        </div>
                        {canCarryForward && (
                            <Button
                                onClick={() => setConfirmCarry(true)}
                                data-testid="carry-forward-btn"
                                variant="outline"
                                className="h-9 text-[12px]"
                            >
                                <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />
                                Carry forward to next handover
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Shift summary */}
            <section
                className="bg-white border border-ntt-border rounded-xl p-5 mb-6"
                data-testid="section-summary"
            >
                <div className="flex items-baseline justify-between mb-2">
                    <h2 className="text-[15px] font-semibold text-[#070F26]">
                        Shift summary
                    </h2>
                    {canEdit && !summary.trim() && (
                        <span className="text-[11px] text-red-700 font-medium">
                            Required to submit
                        </span>
                    )}
                </div>
                {canEdit ? (
                    <textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        onBlur={saveSummary}
                        placeholder="Overall system health, notable events, general notes for the incoming shift…"
                        data-testid="shift-summary-textarea"
                        className="w-full min-h-[120px] rounded-md border border-ntt-border p-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                    />
                ) : (
                    <div
                        data-testid="shift-summary-readonly"
                        className="text-[13px] text-[#2B2F3A] whitespace-pre-wrap"
                    >
                        {handover.shift_summary || (
                            <span className="italic text-ntt-muted">
                                No summary provided.
                            </span>
                        )}
                    </div>
                )}
                {saveError && (
                    <div
                        className="mt-2 flex items-start gap-2 text-[12px] text-red-700"
                        data-testid="summary-save-error"
                    >
                        <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                        <span>{saveError}</span>
                    </div>
                )}
            </section>

            {/* Category sections */}
            <div className="space-y-8">
                {CATEGORIES.map((c) => (
                    <CategorySection
                        key={c.value}
                        category={c.value}
                        label={c.label}
                        items={grouped[c.value] || []}
                        handoverId={id}
                        canEdit={canEdit}
                        onItemAdded={(created) =>
                            setState((s) => ({ ...s, items: [...s.items, created] }))
                        }
                        onItemChanged={(updated) =>
                            setState((s) => ({
                                ...s,
                                items: s.items.map((x) =>
                                    x.item_id === updated.item_id ? updated : x,
                                ),
                            }))
                        }
                        onItemRemoved={(itemId) =>
                            setState((s) => ({
                                ...s,
                                items: s.items.filter((x) => x.item_id !== itemId),
                            }))
                        }
                    />
                ))}
            </div>

            {canSendEmail && (
                <SendEmailDialog
                    open={emailOpen}
                    onOpenChange={setEmailOpen}
                    handover={handover}
                />
            )}

            {/* Confirm: Submit */}
            <AlertDialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
                <AlertDialogContent data-testid="confirm-submit-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Submit this handover?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Once submitted, the handover is locked from further edits. The incoming
                            user (or an admin) will need to acknowledge it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-submit-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-submit-ok"
                            disabled={submitting}
                            onClick={async (e) => {
                                e.preventDefault();
                                await submit();
                                setConfirmSubmit(false);
                            }}
                            className="bg-[#0072BC] hover:bg-[#005a94]"
                        >
                            {submitting ? "Submitting…" : "Submit"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm: Acknowledge */}
            <AlertDialog open={confirmAck} onOpenChange={setConfirmAck}>
                <AlertDialogContent data-testid="confirm-ack-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Acknowledge handover?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Confirm that you have reviewed the shift summary and every category.
                            Ownership of the open items will transfer to you.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-ack-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-ack-ok"
                            onClick={async (e) => {
                                e.preventDefault();
                                await acknowledge();
                                setConfirmAck(false);
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            Acknowledge
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm: Delete draft */}
            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent data-testid="confirm-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the draft handover and any items you have
                            added. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-delete-ok"
                            onClick={async (e) => {
                                e.preventDefault();
                                await remove();
                            }}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete draft
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm: Carry forward */}
            <AlertDialog open={confirmCarry} onOpenChange={setConfirmCarry}>
                <AlertDialogContent data-testid="confirm-carry-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Carry forward to next handover?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {stillOpenCount} still-open item{stillOpenCount === 1 ? "" : "s"} will be
                            copied into a new Draft handover for {handover.shift_name} on the next
                            day. Original items stay untouched. If a Draft already exists for that
                            shift and date, items are appended (duplicates are skipped).
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-carry-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-carry-ok"
                            disabled={carrying}
                            onClick={async (e) => {
                                e.preventDefault();
                                await carryForward();
                            }}
                            className="bg-[#0072BC] hover:bg-[#005a94]"
                        >
                            {carrying ? "Copying…" : "Copy items forward"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}

function fmtDT(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
